import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { usePreviewImage } from '@euler/app/components/media/ImageViewerContext';
import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import {
  ScoreGaugeView,
  useDefaultScoreGaugeStyle,
} from '@euler/app/components/score/ScoreGauge';
import { calculateScoreViewDimension } from '@euler/app/components/score/util';
import { Typography } from '@euler/app/components/typography';
import { usePreInspectionResults } from '@euler/app/flows/report/functions';
import { useDashboardInspectionTemplate } from '@euler/app/flows/task/pages/pre-inspection/functions/useDashboardInspectionFlow';
import { Colors } from '@euler/components/Colors';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsLandscape } from '@euler/functions/responsiveness';
import { useContainerDimensions } from '@euler/functions/useAdaptiveContainerWidth';
import {
  useInventory,
  useInventoryLookup,
} from '@euler/functions/useInventory';
import { MediaObject } from '@euler/model/MediaObject';
import {
  AbnormalLevel,
  OptionValueType,
  SeverityLevel,
  SiteInspectionType,
} from '@euler/model/enum';
import {
  InspectionReportItem,
  InspectionReportItemType,
  MediaInfo,
  VehicleReport,
} from '@euler/model/report';
import { arr2groupmap } from '@euler/utils/array';
import { formatDateTime } from '@euler/utils/formatters';
import { MotiView } from 'moti';
import { ReactNode, memo, useCallback, useMemo } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  ScrollView,
  ScrollViewProps,
  StyleProp,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { ImageStyle } from 'react-native-fast-image';
import { Fill } from 'react-slot-fill';
import { VehicleFacadeBirdView } from '../VehicleFacadeBirdView';
import { DashboardImageView } from './DashboardImageView';
import { DashboardSiteItemList } from './DashboardSiteItemList';
import { PreInspectionSummaryView } from './PreInspectionSummaryView';

const kCellMargin = 20;

type Item = (
  | {
      type: 'dashboard-image';
    }
  | { type: 'score' }
  | {
      type: 'dashboard-items';
      scroll: boolean;
      itemStyle?: StyleProp<ViewStyle>;
      listContainerStyle?: StyleProp<ViewStyle>;
      iconSize?: number;
      iconStyle?: StyleProp<ImageStyle>;
      labelStyle?: StyleProp<TextStyle>;
      statusBadgeStyle?: StyleProp<ViewStyle>;
      statusTextStyle?: StyleProp<TextStyle>;
    }
  | {
      type: 'facade-birdview';
    }
  | {
      type: 'facade-summary';
      separatorLineColor?: string | [string, string];
      infoCellStyle?: StyleProp<ViewStyle>;
      infoValueStyle?: StyleProp<TextStyle>;
      infoLabelStyle?: StyleProp<TextStyle>;
    }
  | {
      type: 'facade-media';
      media: MediaInfo;
    }
) & {
  style?: StyleProp<ViewStyle>;
  cellStyle?: StyleProp<ViewStyle>;
  delay?: number;
};

export const PreInspectionReportViewIpad = memo(
  ({ report }: { report: VehicleReport }) => {
    const { width, height } = useContainerDimensions();
    const { rpx } = useResponsiveContext();
    return (
      <>
        <View
          css={`
            flex: 1;
            justify-content: center;
          `}
        >
          <Content report={report} width={width} height={height} />
        </View>
        <Fill name="reports/preInspection/page-container/footer/left">
          <Typography.Label light color={Colors.White5} size={rpx(18)}>
            服务顾问:{' '}
            {report.preInspection?.technicianName ?? report.serviceAgentName}
          </Typography.Label>
          <Typography.Label light color={Colors.White5} size={rpx(18)}>
            检测时间:{' '}
            {formatDateTime(report.preInspection?.createdAt ?? new Date())}
          </Typography.Label>
        </Fill>
      </>
    );
  },
);

const Content = memo(
  ({
    width,
    height,
    report,
    style,
  }: {
    width: number;
    height: number;
    report: VehicleReport;
    style?: StyleProp<ViewStyle>;
  }) => {
    const appNavigation = useAppNavigation();
    const { rpx } = useResponsiveContext();
    const { overlays } = usePreInspectionResults(report.preInspection);
    const previewImage = usePreviewImage();
    const [inventory] = useInventory();
    const inventoryQuery = useInventoryLookup(inventory);
    const cellWidth = Math.round(width / 2.75);
    const cellHeight = Math.round(height * 0.75);
    const template = useDashboardInspectionTemplate();
    const secondaryCellWidth = Math.round(width * 0.4) - 2 * rpx(kCellMargin);
    const primaryCellWidth = Math.round(width * 0.6) - 2 * rpx(kCellMargin);

    const medias = useMemo(
      () => (report.preInspection?.items ?? []).flatMap(x => x.medias),
      [report.preInspection?.items],
    );

    const dashboardItems = useMemo<InspectionReportItem[]>(() => {
      let items: InspectionReportItem[] = [];
      if (template == null) {
        items = report.preInspection?.items ?? [];
      } else {
        const map = arr2groupmap(
          report.preInspection?.items ?? [],
          x => x.siteId,
        );
        const siteIds = template.conf.categories.flatMap(c =>
          c.groups.flatMap(g => g.siteIds),
        );
        for (const siteId of siteIds) {
          const site = inventoryQuery.getSiteById(siteId);
          if (site == null) continue;
          if (map.has(siteId)) {
            items.push(...map.get(siteId)!);
          } else {
            items.push({
              siteId,
              type: InspectionReportItemType.Normal,
              inspectionType: SiteInspectionType.Dashboard,
              siteName: site.name,
              abnormalLevel: AbnormalLevel.Fine,
              severityLevel: SeverityLevel.None,
              resultDataType: OptionValueType.String,
              siteInfluenceFactor: 0,
              siteItemInfluenceFactor: 0,
              data: [],
              medias: [],
            });
          }
        }
      }

      return items;
    }, [inventoryQuery, report.preInspection?.items, template]);

    const isLandscape = useIsLandscape();
    const scaleFactor = secondaryCellWidth / rpx(370);

    const primaryCells = useMemo<Item[]>(() => {
      return isLandscape
        ? [
            {
              type: 'dashboard-image',
            },
            {
              type: 'dashboard-items',
              scroll: true,
              iconSize: rpx(24),
              labelStyle: {
                fontFamily: FontFamily.NotoSans.Regular,
                fontSize: rpx(18),
                lineHeight: rpx(28),
                color: 'rgba(0, 0, 0, 0.7)',
                marginLeft: rpx(15),
              },
              statusBadgeStyle: {
                height: rpx(24),
                borderRadius: rpx(12),
                paddingLeft: rpx(12),
                paddingRight: rpx(12),
              },
              statusTextStyle: {
                fontSize: rpx(15),
                lineHeight: rpx(22),
              },
            },
            {
              type: 'facade-birdview',
              cellStyle: {
                justifyContent: 'center',
                alignItems: 'center',
              },
            },
            { type: 'facade-summary' },
            ...medias.map<Item>(media => ({
              type: 'facade-media',
              media,
              style: { flex: 1 },
              delay: 0,
            })),
          ]
        : [
            {
              type: 'dashboard-image',
              cellStyle: {
                height: primaryCellWidth * 0.8,
              },
              delay: 0,
            },
            {
              type: 'dashboard-items',
              scroll: false,
              delay: 0,
              cellStyle: {
                paddingLeft: rpx(4),
                paddingRight: rpx(4),
                paddingTop: rpx(10),
                paddingBottom: rpx(10),
              },
              listContainerStyle: {
                paddingBottom: 0,
              },
              itemStyle: {
                paddingTop: rpx(3),
                paddingBottom: rpx(3),
              },
              labelStyle: {
                fontSize: rpx(13),
              },
            },
            ...medias.map<Item>(media => ({
              type: 'facade-media',
              media,
              style: { height: primaryCellWidth * 0.8 },
              delay: 0,
            })),
          ];
    }, [isLandscape, medias, primaryCellWidth, rpx]);

    const secondaryCells = useMemo<Item[]>(() => {
      return [
        {
          type: 'score',
          cellStyle: { backgroundColor: 'transparent', marginBottom: 0 },
          delay: 0,
        },
        {
          type: 'facade-summary',
          delay: 0,
          cellStyle: {
            backgroundColor: 'transparent',
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            paddingBottom: 0,
            marginBottom: secondaryCellWidth >= rpx(370) ? 10 : 0,
          },
          infoCellStyle: { marginBottom: rpx(10) },
          infoLabelStyle: {
            color: Colors.White3,
            fontSize: scaleFactor * rpx(18),
          },
          infoValueStyle: {
            color: Colors.White8,
            fontSize: scaleFactor * rpx(64),
            marginBottom: 0,
          },
          separatorLineColor: [
            'rgba(255, 255, 255, 0.1)',
            'rgba(255, 255, 255, 0)',
          ],
        },
        {
          type: 'facade-birdview',
          delay: 0,
          cellStyle: {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'transparent',
          },
        },
      ];
    }, [rpx, scaleFactor, secondaryCellWidth]);

    const onMediaPress = useCallback(
      (media: MediaInfo) => {
        if (/image/i.test(media.type)) {
          const images = medias.filter(x => /image/i.test(x.type));
          const index = images.findIndex(x => x.id === media.id);
          previewImage({
            imageUrls: medias.map(x => x.url),
            index,
          });
        } else {
          appNavigation.navigate('_mediaPreview', {
            type: 'video',
            url: media.url,
          });
        }
      },
      [appNavigation, medias, previewImage],
    );

    const score = report.preInspection?.score ?? 100;
    const renderItemContent = useCallback(
      (item: Item) => {
        switch (item.type) {
          case 'score':
            return (
              <ScoreView
                score={score}
                width={secondaryCellWidth}
                height={secondaryCellWidth * 0.8}
                style={item.style}
              />
            );
          case 'dashboard-image':
            return (
              <DashboardImageView
                report={report}
                css={`
                  flex: 1;
                  border-radius: ${rpx(20)}px;
                `}
                style={item.style}
              />
            );
          case 'dashboard-items':
            return (
              <MaybeScrollView
                scrollable={item.scroll}
                showsVerticalScrollIndicator={Platform.OS === 'web'}
                contentContainerStyle={{
                  flexGrow: 1,
                  justifyContent: 'center',
                }}
                style={item.style}
              >
                <DashboardSiteItemList
                  items={dashboardItems}
                  iconSize={item.iconSize}
                  inventoryQuery={inventoryQuery}
                  labelStyle={item.labelStyle}
                  statusBadgeStyle={item.statusBadgeStyle}
                  statusTextStyle={item.statusTextStyle}
                  iconStyle={item.iconStyle}
                  style={item.listContainerStyle}
                  itemStyle={item.itemStyle}
                />
              </MaybeScrollView>
            );
          case 'facade-birdview':
            return (
              <VehicleFacadeBirdView
                overlays={overlays}
                bgColor={null}
                size={Math.min(cellWidth - rpx(40), height)}
                style={item.style}
              />
            );
          case 'facade-summary':
            return (
              <PreInspectionSummaryView
                report={report}
                inventoryQuery={inventoryQuery}
                infoCellStyle={item.infoCellStyle}
                infoLabelStyle={item.infoLabelStyle}
                infoValueStyle={item.infoValueStyle}
                separatorLineColor={item.separatorLineColor}
                css={`
                  flex: 1;
                `}
                style={item.style}
              />
            );
          case 'facade-media':
            return (
              <InspectedItemMediaView
                media={item.media}
                onPress={onMediaPress}
                style={item.style}
              />
            );
        }
      },
      [
        cellWidth,
        dashboardItems,
        height,
        inventoryQuery,
        onMediaPress,
        overlays,
        report,
        rpx,
        score,
        secondaryCellWidth,
      ],
    );

    const itemStyle = useMemo<StyleProp<ViewStyle>>(() => {
      return isLandscape
        ? {
            width: cellWidth,
            height: cellHeight,
          }
        : {
            marginBottom: kCellMargin,
          };
    }, [cellHeight, cellWidth, isLandscape]);

    const renderItem: ListRenderItem<Item> = useCallback(
      ({ item, index }) => {
        return (
          <Cell
            index={index}
            delay={item.delay}
            style={[itemStyle, item.cellStyle]}
          >
            {renderItemContent(item)}
          </Cell>
        );
      },
      [itemStyle, renderItemContent],
    );

    const getItemLayout = useCallback(
      (_: any, index: number) => {
        const length = isLandscape
          ? cellWidth + 2 * kCellMargin
          : cellHeight + kCellMargin;
        return {
          length,
          offset: length * index,
          index,
        };
      },
      [cellHeight, cellWidth, isLandscape],
    );

    if (isLandscape) {
      return (
        <FlatList
          horizontal
          showsHorizontalScrollIndicator={Platform.OS === 'web'}
          contentContainerStyle={{
            alignItems: 'center',
          }}
          css={`
            flex-grow: 0;
            height: ${height}px;
          `}
          style={style}
          data={primaryCells}
          renderItem={renderItem}
          getItemLayout={isLandscape ? getItemLayout : undefined}
        />
      );
    }

    return (
      <HStack
        css={`
          flex: 1;
          align-items: stretch;
        `}
      >
        <FlatList
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          css={`
            flex: 2;
          `}
          style={style}
          data={secondaryCells}
          renderItem={renderItem}
          getItemLayout={getItemLayout}
        />
        <FlatList
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          css={`
            flex: 3;
          `}
          style={style}
          data={primaryCells}
          renderItem={renderItem}
          getItemLayout={getItemLayout}
        />
      </HStack>
    );
  },
);

const InspectedItemMediaView = memo(
  ({
    media,
    style,
    onPress,
  }: {
    media: MediaInfo;
    style?: StyleProp<ViewStyle>;
    onPress?: (media: MediaInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const handlePress = useCallback(() => {
      onPress?.(media);
    }, [media, onPress]);

    const mediaObject = useMemo<MediaObject>(() => {
      return {
        id: media.id,
        url: media.url,
        type: /image/i.test(media.type) ? 'image' : 'video',
        coverUrl: media.coverUrl,
        title: media.title,
        subTitle: media.subTitle,
        description: media.remark,
      };
    }, [media]);

    return (
      <View style={style}>
        <MediaCoverView
          media={mediaObject}
          playIconSize={rpx(48)}
          onPress={handlePress}
          TouchableComponent={TouchableOpacity}
          css={`
            border-radius: ${rpx(20)}px;
            overflow: hidden;
          `}
        />
      </View>
    );
  },
);

const Cell = memo(
  ({
    index,
    style,
    children,
    delay,
  }: {
    index: number;
    style?: StyleProp<ViewStyle>;
    children?: ReactNode;
    delay?: number;
  }) => {
    const { rpx } = useResponsiveContext();
    if (delay === 0) {
      return (
        <View
          css={`
            background-color: #fff;
            border-radius: ${rpx(32)}px;
            padding: ${rpx(20)}px;
            margin-left: ${rpx(kCellMargin)}px;
            margin-right: ${rpx(kCellMargin)}px;
            overflow: hidden;
          `}
          style={style}
        >
          {children}
        </View>
      );
    }
    return (
      <MotiView
        from={{ opacity: 0, translateX: rpx(250) }}
        animate={{ opacity: 1, translateX: 0 }}
        transition={{ type: 'timing', duration: 500 }}
        delay={delay ?? index * 100}
        css={`
          background-color: #fff;
          border-radius: ${rpx(32)}px;
          padding: ${rpx(20)}px;
          margin-left: ${rpx(kCellMargin)}px;
          margin-right: ${rpx(kCellMargin)}px;
          overflow: hidden;
        `}
        style={style}
      >
        {children}
      </MotiView>
    );
  },
);

const MaybeScrollView = memo(
  ({
    scrollable,
    children,
    ...props
  }: ScrollViewProps & { scrollable?: boolean }) => {
    if (scrollable) {
      return <ScrollView {...props}>{children}</ScrollView>;
    }
    return <>{children}</>;
  },
);

const ScoreView = memo(
  (props: {
    score: number;
    width: number;
    height: number;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const dimens = calculateScoreViewDimension(props.width, props.height);
    const width = Math.round(dimens.width);
    const height =
      Math.round(dimens.height) * (width / Math.round(dimens.width));
    const scaleFactor = width / rpx(370);
    const defaultTheme = useDefaultScoreGaugeStyle();
    const theme = useMemo(() => {
      return {
        ...defaultTheme,
        scoreLabelColor: 'rgba(255, 255, 255, 0.75)',
        tickLabelColor: 'rgba(255, 255, 255, 0.5)',
        tickLabelFontSize: rpx(14),
        scoreLabelFontSize: rpx(24) * scaleFactor,
        scoreFontSize: rpx(72) * scaleFactor,
      };
    }, [defaultTheme, rpx, scaleFactor]);

    return (
      <View
        css={`
          align-items: center;
          justify-content: center;
          height: ${height}px;
        `}
      >
        <ScoreGaugeView
          score={props.score}
          width={width}
          height={height * (Math.min(width, rpx(460)) / width)}
          theme={theme}
          label="外观评分"
          scoreTextContainerStyle={{
            transform: [{ translateY: rpx(10) }],
          }}
        />
      </View>
    );
  },
);
